import { decodeBase64UrlBytes } from '@/lib/utils/base64url';

export interface EncryptedRecord {
  __enc: 1;
  owner: string;
  iv: ArrayBuffer;
  ct: ArrayBuffer;
}

// Keys live here and nowhere else: never in storage of any kind, so a signed-out browser
// holds only ciphertext.
const keys = new Map<string, CryptoKey>();
const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function importHistoryKey(historyKey: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', decodeBase64UrlBytes(historyKey), 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);
}

export function retainKey({ owner, key }: { owner: string; key: CryptoKey }) {
  keys.set(owner, key);
}

export async function holdKey({ owner, historyKey }: { owner: string; historyKey: string }) {
  retainKey({ owner, key: await importHistoryKey(historyKey) });
}

export function dropKeys() {
  keys.clear();
}

export function hasKey(owner: string) {
  return keys.has(owner);
}

export function isEncrypted(value: unknown): value is EncryptedRecord {
  return typeof value === 'object' && value !== null && (value as { __enc?: unknown }).__enc === 1;
}

function keyFor(owner: string) {
  const key = keys.get(owner);
  if (!key) throw new Error(`history key not held for ${owner}`);
  return key;
}

export async function encryptJson({
  owner,
  value,
  context = [],
}: {
  owner: string;
  value: unknown;
  context?: string[];
}): Promise<EncryptedRecord> {
  const key = keyFor(owner);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: encoder.encode(JSON.stringify([owner, ...context])) },
    key,
    encoder.encode(JSON.stringify(value)),
  );
  return { __enc: 1, owner, iv: iv.buffer, ct };
}

export async function decryptJson<T>({
  record,
  context = [],
}: {
  record: EncryptedRecord;
  context?: string[];
}): Promise<T> {
  const plain = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: record.iv,
      additionalData: encoder.encode(JSON.stringify([record.owner, ...context])),
    },
    keyFor(record.owner),
    record.ct,
  );
  return JSON.parse(decoder.decode(plain)) as T;
}
